import { Subscription } from 'rxjs';
import { AppConstants } from '@unpispas/upp-defs';

import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';

import { SessionType } from '../types';
import { QrCodeType } from '../types';
import { TicketType } from '../types';

import type { Session } from './session';
import { QrCode } from './qrcode';
import { Ticket } from './ticket';

import { AskWaiterView, AskWaiterViewProxy } from '../views/askwaiter';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

const _schema = {
    name: 'ASKWAITER',
    fields: [
        { name: 'created', type: 'date', volatile: true },
        { name: 'updated', type: 'date', volatile: true },
        { name: 'status', type: 'string', default: 'AC' },
        { name: 'session', type: 'objid' },
        { name: 'qrcode', type: 'objid' },
        { name: 'reason', type: 'string' },
        { name: 'amount', type: 'number' },
        { name: 'ticket', type: 'objid' },
    ],
    relate: [
        { direction: '>', target: 'SESSION', by: 'session', name: 'session', reason: 'session', child: false, class: SessionType },
        { direction: '>', target: 'QRCODE', by: 'qrcode', name: 'qrcode', reason: 'qrcode', child: false, class: QrCodeType },
        { direction: '>', target: 'TICKET', by: 'ticket', name: 'ticket', reason: 'ticket', child: false, class: TicketType },
    ],
} as const;

const _AskWaiterClass = CreateObject(_schema);
if (!_AskWaiterClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class AskWaiter extends _AskWaiterClass {
    private _oneminute_subscription: Subscription | null = null;
    private _isrecent = true;

    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});

        // Setter + objid ⇒ ToUpdate; the changelog OnChange is then ignored.
        if (!this.objid){
            this.created = new Date();
            this.updated = new Date();
        }

        this._IsRecentCheck();
    }

    Copy(store: Array<DataObject> = []): AskWaiter {
        return this._Copy(store) as AskWaiter;
    }

    override get viewProxy(): AskWaiterView {
        return AskWaiterViewProxy(this, this.data);
    }

    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf){
            return;
        }

        if (relation === 'qrcode' && related instanceof QrCode){
            if (related === this.qrcode){
                related.AddRequest(this);
            }
            else {
                related.DelRequest(this);
            }
            return;
        }

        // Changelog rows lack `TICKET.requests` (only `QRCODE.requests` was wired before).
        if (relation === 'ticket' && related instanceof Ticket){
            if (related === this.ticket){
                related.AddRequest(this);
            }
            else {
                related.DelRequest(this);
            }
        }
    }

    protected override OnUpdate(info: any): boolean {
        const changed = super.OnUpdate(info);
        if (changed) {
            this._IsRecentCheck();
        }

        return changed;
    }

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        if (this.qrcode){
            if ((this.status == 'DE') && !this.Commited){
                this.qrcode.DelRequest(this);
            }
            else {
                this.qrcode.DoRefresh(this.table);
            }
        }
    }

    get ticket(): Ticket | null {
        return (this._children['ticket'] as Ticket) || null;
    }

    set ticket(value: Ticket | null){
        if (this.SetChild('ticket', value, 'ticket')){
            this.ToUpdate = true;
        }

        this.qrcode = value?.qrcode ?? null;
    }

    get qrcode(): QrCode | null {
        return (this._children['qrcode'] as QrCode) || null;
    }

    set qrcode(value: QrCode | null){
        if (this.SetChild('qrcode', value, 'qrcode')){
            this.ToUpdate = true;
        }
    }

    get reason(): string | null {
        return this['__base__reason'];
    }

    set reason(value: string | null){
        if (this['__base__reason'] !== value){
            this['__base__reason'] = value;
            this.ToUpdate = true;
        }
    }

    get amount(): number | null {
        return this['__base__amount'];
    }

    set amount(value: number | null){
        if (this['__base__amount'] !== value){
            this['__base__amount'] = value;
            this.ToUpdate = true;
        }
    }

    get IsValid(): boolean {
        if (!this.status || this.status == 'DE'){
            return false;
        }

        if (this.ticket){
            if (!this.ticket.IsValid){
                return false;
            }

            if (this.ticket.PaidFlag && (['PAYCASH', 'PAYCARD', 'PAYMIXED'].includes(this.reason || ''))){
                return false;
            }
        }

        return true;
    }

    get IsRecent(): boolean {
        const ref = this.updated || this.created;
        if (!ref){
            return false;
        }

        return Date.now() - ref.getTime() < AppConstants.recentMs;
    }

    /****************************/
    /* PRIVATE METHODS          */
    /****************************/

    private _IsRecentCheck(): void {
        const recent = this.IsRecent;
        if (!recent){
            if (this._oneminute_subscription){
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
            this._isrecent = false;
            return;
        }

        this._isrecent = recent;
        if (!this._oneminute_subscription){
            this._oneminute_subscription = this.data.clock.OnOneMinuteTick.subscribe(() => {
                this._onOneMinuteTick();
            });
        }
    }

    private _onOneMinuteTick(): void {
        if (!this.IsValid){
            if (this._oneminute_subscription){
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
            return;
        }

        const isRecent = this.IsRecent;
        if (!isRecent){
            if (this._oneminute_subscription){
                this._oneminute_subscription.unsubscribe();
                this._oneminute_subscription = null;
            }
        }

        if (this._isrecent === isRecent){
            return;
        }

        this._isrecent = isRecent;
        this.qrcode?.DoRefresh(this.table);
    }
}
